type ZendeskZE = (...args: unknown[]) => void;

const ZENDESK_SNIPPET_ID = 'ze-snippet';
const ZENDESK_SNIPPET_SRC =
  'https://static.zdassets.com/ekr/snippet.js?key=3c13e7a6-de92-415c-b4a9-0a8b11da8a60';
const LOAD_TIMEOUT_MS = 8000;

let loadPromise: Promise<ZendeskZE> | null = null;

function getZE(): ZendeskZE | null {
  const zE = (window as unknown as { zE?: ZendeskZE }).zE;
  return typeof zE === 'function' ? zE : null;
}

function setZESettings(jwtFn?: (callback: (jwt: string) => void) => void) {
  (window as unknown as { zESettings?: unknown }).zESettings = {
    webWidget: {
      color: {
        theme: '#045C6F',
        launcherText: '#fafafa',
        header: '#045C6F',
      },
      helpCenter: {
        originalArticleButton: false,
      },
      position: { horizontal: 'right', vertical: 'top' },
      offset: { vertical: '80px', horizontal: '25px' },
      ...(jwtFn ? { authenticate: { jwtFn } } : {}),
    },
  };
}

function injectWidgetStyles() {
  if (document.getElementById('ze-custom-styles')) return;
  const style = document.createElement('style');
  style.id = 'ze-custom-styles';
  style.textContent = `#webWidget { width: 768px !important } @media (max-width: 768px) { #webWidget { width: 100% !important; min-width: unset } }`;
  document.head.appendChild(style);
}

function injectSnippet() {
  if (document.getElementById(ZENDESK_SNIPPET_ID)) return;
  const s = document.createElement('script');
  s.id = ZENDESK_SNIPPET_ID;
  s.src = ZENDESK_SNIPPET_SRC;
  s.async = true;
  document.head.appendChild(s);
}

function applyJwtFn(
  zE: ZendeskZE,
  jwtFn: (callback: (jwt: string) => void) => void
) {
  zE('webWidget', 'updateSettings', {
    webWidget: { authenticate: { jwtFn } },
  });
}

export async function loadZendesk(
  jwtFn?: (callback: (jwt: string) => void) => void
): Promise<ZendeskZE> {
  const existing = getZE();
  if (existing) {
    if (jwtFn) applyJwtFn(existing, jwtFn);
    return existing;
  }

  if (loadPromise) {
    if (jwtFn) {
      return loadPromise.then((zE) => {
        applyJwtFn(zE, jwtFn);
        return zE;
      });
    }
    return loadPromise;
  }

  loadPromise = new Promise<ZendeskZE>((resolve, reject) => {
    setZESettings(jwtFn);
    injectSnippet();

    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      const zE = getZE();
      if (zE) {
        window.clearInterval(interval);
        injectWidgetStyles();
        resolve(zE);
        return;
      }

      if (Date.now() - startedAt > LOAD_TIMEOUT_MS) {
        window.clearInterval(interval);
        loadPromise = null;
        reject(new Error('Zendesk did not load in time'));
      }
    }, 100);
  });

  return loadPromise;
}

export async function openZendeskFromNavbar(): Promise<void> {
  const zE = await loadZendesk();
  zE('webWidget', 'show');
  zE('webWidget', 'open');
}

export function helpCenterReauthenticate(): void {
  const zE = getZE();
  if (zE) zE('webWidget', 'helpCenter:reauthenticate');
}
